import React, { useState } from 'react';
import { PlusIcon, ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { MatterDetailed } from '../../../../../types/matters';
import type { ClientTrustLedger, TrustTransaction, TrustPaymentMethod } from '../../../../billing/types/billing.types';
import { TRUST_TRANSACTION_TYPE_LABELS } from '../../../../billing/types/billing.types';

interface TrustAccountSectionProps {
  matter: MatterDetailed;
  trustLedger: ClientTrustLedger | null;
  trustTransactions: TrustTransaction[];
  stats: {
    trustBalance: number;
    trustTotalDeposits: number;
    trustTotalDisbursements: number;
  };
  recordTrustDeposit: (dto: any) => Promise<TrustTransaction>;
  loading: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TrustAccountSection: React.FC<TrustAccountSectionProps> = ({
  matter, trustLedger, trustTransactions, stats, recordTrustDeposit,
}) => {
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [trustAmount, setTrustAmount] = useState('');
  const [trustDescription, setTrustDescription] = useState('');
  const [trustSource, setTrustSource] = useState('');
  const [trustPurpose, setTrustPurpose] = useState('');
  const [trustPaymentMethod, setTrustPaymentMethod] = useState<TrustPaymentMethod>('check');
  const [trustRefNumber, setTrustRefNumber] = useState('');
  const [trustSubmitting, setTrustSubmitting] = useState(false);
  const [trustError, setTrustError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSubmitDeposit = async () => {
    if (!trustAmount || Number(trustAmount) <= 0) { setTrustError('Amount is required.'); return; }
    if (!trustDescription.trim()) { setTrustError('Description is required.'); return; }
    if (!trustSource.trim()) { setTrustError('Source / payor is required.'); return; }
    if (!trustLedger) { setTrustError('No trust ledger found for this engagement. Create one in Trust Administration first.'); return; }
    setTrustSubmitting(true);
    setTrustError(null);
    try {
      await recordTrustDeposit({
        trust_account_id: trustLedger.trust_account_id,
        client_id: trustLedger.client_id,
        matter_id: matter.id,
        amount: Number(trustAmount),
        description: trustDescription,
        source_or_payee: trustSource,
        purpose: trustPurpose || undefined,
        payment_method: trustPaymentMethod,
        reference_number: trustRefNumber || undefined,
      });
      setShowDepositForm(false);
      setTrustAmount(''); setTrustDescription(''); setTrustSource('');
      setTrustPurpose(''); setTrustRefNumber(''); setTrustPaymentMethod('check');
      setSuccessMsg('Deposit recorded');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err) {
      setTrustError(err instanceof Error ? err.message : 'Failed to record deposit');
    } finally {
      setTrustSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldCheckIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
          Trust Account (IOLTA)
        </h4>
        {trustLedger && (
          <button
            onClick={() => setShowDepositForm(!showDepositForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Record Deposit
          </button>
        )}
      </div>

      {/* Success toast */}
      {successMsg && (
        <div className="lex-success-toast flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">{successMsg}</span>
        </div>
      )}

      {/* Trust Ledger Summary */}
      {trustLedger ? (
        <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Current Balance</div>
              <div className="text-lg font-bold text-teal-900 dark:text-teal-100 tabular-nums">
                {formatCurrency(stats.trustBalance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Total Deposits</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatCurrency(stats.trustTotalDeposits)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Total Disbursements</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatCurrency(stats.trustTotalDisbursements)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Ledger Status</div>
              <div className="mt-1">
                <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                  trustLedger.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {trustLedger.status.charAt(0).toUpperCase() + trustLedger.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          No trust ledger set up for this engagement. Create one via Trust Administration to enable deposits.
        </div>
      )}

      {/* Inline Deposit Form — animated expand */}
      {showDepositForm && trustLedger && (
        <div className="lex-form-panel bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Amount *</label>
              <input type="number" step="0.01" min="0" value={trustAmount} onChange={(e) => setTrustAmount(e.target.value)} placeholder="0.00"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Source / Payor *</label>
              <input type="text" value={trustSource} onChange={(e) => setTrustSource(e.target.value)} placeholder="e.g. Client name"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Payment Method</label>
              <select value={trustPaymentMethod} onChange={(e) => setTrustPaymentMethod(e.target.value as TrustPaymentMethod)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <option value="check">Check</option>
                <option value="wire">Wire Transfer</option>
                <option value="ach">ACH</option>
                <option value="eft">EFT</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description *</label>
            <textarea value={trustDescription} onChange={(e) => setTrustDescription(e.target.value)} rows={2} placeholder="Describe the deposit (e.g. retainer deposit, settlement funds)..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Purpose</label>
              <input type="text" value={trustPurpose} onChange={(e) => setTrustPurpose(e.target.value)} placeholder="Legal purpose for this deposit"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Reference Number</label>
              <input type="text" value={trustRefNumber} onChange={(e) => setTrustRefNumber(e.target.value)} placeholder="Check #, wire ref, etc."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          </div>
          {trustError && <p className="text-xs text-red-500">{trustError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowDepositForm(false); setTrustError(null); }}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={handleSubmitDeposit} disabled={trustSubmitting}
              className="px-4 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {trustSubmitting ? 'Recording...' : 'Record Deposit'}
            </button>
          </div>
        </div>
      )}

      {/* Trust Transactions Table */}
      {trustTransactions.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Type</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Source / Payee</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Balance</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {trustTransactions.map((txn, i) => (
                <tr key={txn.trust_transaction_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                    {formatDate(txn.transaction_date)}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {TRUST_TRANSACTION_TYPE_LABELS[txn.transaction_type] || txn.transaction_type}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">
                    {txn.description}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {txn.source_or_payee || '-'}
                  </td>
                  <td className={`px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap ${
                    txn.direction === 'credit'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {txn.direction === 'credit' ? '+' : '-'}{formatCurrency(Number(txn.amount))}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                    {formatCurrency(Number(txn.balance_after))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                      txn.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                      txn.status === 'reversed' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
                      txn.status === 'void' ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                    }`}>
                      {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrustAccountSection;
